import { useEffect, useRef, useState, type JSX, type ReactNode } from 'react'

import { LOCALES } from '@lensmira/i18n'

import { DENSITIES, type Density } from './density.js'
import { useTranslation } from './i18n.js'

/**
 * The counter's app shell. `DESIGN-TOKENS.md` §16.3.
 *
 * > **App shell**: Left rail on desktop, bottom bar on mobile. Branch switcher and work-queue count
 * > always visible.
 *
 * Both halves of that sentence are load-bearing and neither is decoration.
 *
 * **The branch is always on screen** because `BR-05` scopes stock, cash, staff and every sale to a
 * branch, and `BR-08` records it on the sale itself. A dispenser who has not noticed which branch
 * they are in books the sale, the stock movement and the cash against the wrong one, and nothing
 * downstream can tell it happened. Showing it costs a strip of screen; not showing it costs a
 * reconciliation.
 *
 * **The work-queue count is always on screen** because §16.3 makes the queue "the default landing
 * surface for staff": a job waiting on somebody is the unit of work in a shop, and a count that is
 * only visible once you navigate to it is a count nobody sees.
 *
 * The rail is a `<nav>` with real buttons rather than a list of divs, because the counter is
 * keyboard-first: release gate 1 measures a prescription captured without touching the mouse, and a
 * shell that cannot be tabbed through undoes that on the very next screen.
 *
 * ---
 *
 * **The rail is grouped and the mobile bar is five destinations and a More sheet**, which is
 * `decisions/0031`, written after photographing this shell at 1280 and at 390. Twenty-six
 * destinations in one flat list ran past the fold and had to be read label by label; at 390 the
 * same list became a horizontal scroller with a native scrollbar and labels on two lines, which is
 * not what §16.3 means by a bottom bar.
 *
 * **Both are in the DOM at every width, and CSS shows one.** `display: none` takes an element out
 * of the accessibility tree and out of the tab order, so only one is ever reachable in a browser.
 * The alternative, asking `matchMedia` and rendering one, puts the navigation behind a JavaScript
 * measurement, and the cost of being wrong is a till with no way to leave the screen it is on. The
 * cost of this way is that jsdom, which has no CSS, sees both: a test querying the rail scopes
 * itself to the navigation it means, which `shell.test.tsx` does by name.
 */

/** Each nav writes its classes out, because a template hole is invisible to the class-name guard. */
const RAIL = {
  item: 'lm-rail__item',
  active: 'lm-rail__item--active',
  glyph: 'lm-rail__glyph',
  label: 'lm-rail__label',
  count: 'lm-rail__count',
} as const

const BAR = {
  item: 'lm-bar__item',
  active: 'lm-bar__item--active',
  glyph: 'lm-bar__glyph',
  label: 'lm-bar__label',
  count: 'lm-bar__count',
} as const

export interface ShellDestination {
  readonly id: string
  readonly label: string
  /** A short glyph. Real icons are `OPEN.md` §3.2, still outstanding, so this is deliberately text. */
  readonly glyph: string
  /** Which rail section it sits in. One of `groups`. */
  readonly group: string
  /** Shown as a badge. The work queue's count is the one §16.3 names. */
  readonly count?: number
}

export interface ShellGroup {
  readonly id: string
  readonly label: string
}

export interface ShellBranch {
  readonly id: string
  readonly name: string
}

export interface ShellProps {
  readonly destinations: readonly ShellDestination[]
  /** The sections, in rail order. A group no destination is in is not rendered at all. */
  readonly groups: readonly ShellGroup[]
  /** The ids the bottom bar carries, in bar order. The counter loop; see `BOTTOM_BAR` in `App`. */
  readonly primary: readonly string[]
  readonly current: string
  readonly onNavigate: (id: string) => void
  readonly branchName: string
  /**
   * The branches this person may work at, so the name in the bar is the switcher itself.
   *
   * §16.3 says "**Branch switcher** and work-queue count always visible", and for a while the bar
   * held a name and the switcher sat in the page's own toolbar: two places saying which branch,
   * one of them a control and one of them not. A shop with one branch gets the name, because a
   * select with one option is a control that does nothing.
   */
  readonly branches?: readonly ShellBranch[]
  readonly onBranchChange?: (branchId: string) => void
  readonly currentBranchId?: string
  readonly staffName: string
  readonly onSignOut: () => void
  /**
   * Rendered beside the branch, so a till with money in it is visible from every screen.
   *
   * **Three states, and `undefined` is the one this was missing.** It was `boolean` defaulting to
   * `false`, so "nobody has told me" rendered as "the drawer is closed", which is an assertion the
   * shell had no read to support. A role without `cash_session_open_close` is refused the drawer
   * read, and `App` swallows that refusal on the quiet eager load, so every optician, accountant
   * and read-only account in the product was shown a closed drawer over whatever the till was
   * actually doing. Walking the counter as four roles is what found it; no test had a caller
   * without the row.
   */
  readonly cashSessionOpen: boolean | undefined
  readonly density: Density
  readonly onDensityChange: (density: Density) => void
  /**
   * `LOC-002`'s per-user half. The empty string is "as the shop is set", which is a third state and
   * not English: `resolveLocale` falls through to the tenant's setting when this person has not
   * chosen, and choosing English would override it.
   *
   * Beside the density chooser because that is where this product already keeps a per-user display
   * preference, and because somebody who cannot read the screen needs the control to be where they
   * last saw one.
   */
  readonly language: string
  readonly onLanguageChange: (language: string) => void
  /** True while the change is in the write queue, so the select cannot be driven twice. */
  readonly languagePending?: boolean
  /** §16.5's queue indicator, beside the branch. Absent when the queue is clean. */
  readonly queueFlag?: ReactNode
  readonly children: ReactNode
}

export function Shell({
  destinations,
  groups,
  primary,
  current,
  onNavigate,
  branchName,
  branches,
  onBranchChange,
  currentBranchId,
  staffName,
  onSignOut,
  cashSessionOpen,
  density,
  onDensityChange,
  language,
  onLanguageChange,
  languagePending = false,
  queueFlag,
  children,
}: ShellProps): JSX.Element {
  const { t } = useTranslation()
  const [moreOpen, setMoreOpen] = useState(false)
  const moreButton = useRef<HTMLButtonElement>(null)
  const sheet = useRef<HTMLDivElement>(null)

  /**
   * Escape closes it and focus goes back to the button that opened it.
   *
   * A sheet covering the whole screen with no way out but a tap is the one state on a counter
   * tablet that reads as a frozen app, and the keyboard path is release gate 1's stance rather
   * than a nicety.
   */
  useEffect(() => {
    if (!moreOpen) return
    sheet.current?.focus()
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      setMoreOpen(false)
      moreButton.current?.focus()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
    }
  }, [moreOpen])

  const byId = new Map(destinations.map((destination) => [destination.id, destination]))
  /**
   * The bar carries the primary ids this person actually holds, in `primary` order rather than rail
   * order. A role granted none of them gets a bar of nothing but More, which is honest: a
   * stock-only account has no counter loop.
   */
  const barItems = primary
    .map((id) => byId.get(id))
    .filter((destination): destination is ShellDestination => destination !== undefined)

  /** A section with nothing in it is not rendered: permissions can empty one. */
  const sections = groups
    .map((group) => ({
      group,
      items: destinations.filter((destination) => destination.group === group.id),
    }))
    .filter((section) => section.items.length > 0)

  const navigate = (id: string): void => {
    setMoreOpen(false)
    onNavigate(id)
  }

  const entry = (destination: ShellDestination, c: typeof RAIL | typeof BAR): JSX.Element => {
    const active = destination.id === current
    return (
      <button
        type="button"
        className={active ? `${c.item} ${c.active}` : c.item}
        // `aria-current` rather than only a class, so the active section is announced and not merely
        // coloured. A colour-only state is invisible to a screen reader and to anyone who cannot
        // distinguish the two greens.
        aria-current={active ? 'page' : undefined}
        onClick={() => {
          navigate(destination.id)
        }}
      >
        <span className={c.glyph} aria-hidden="true">
          {destination.glyph}
        </span>
        <span className={c.label}>{destination.label}</span>
        {destination.count !== undefined && destination.count > 0 ? (
          <span className={c.count}>
            {/* A count is a number, and numbers never mirror (§6). Only the digits are an island:
                the hidden word beside it is the locale's. */}
            <span dir="ltr">{destination.count}</span>
            <span className="lm-visually-hidden"> {t('shell.waiting')}</span>
          </span>
        ) : null}
      </button>
    )
  }

  return (
    <div className="lm-shell">
      <nav className="lm-rail" aria-label={t('shell.sections')}>
        <div className="lm-rail__brand" aria-hidden="true">
          Lensmira
        </div>
        {sections.map(({ group, items }) => (
          <div className="lm-rail__group" key={group.id}>
            {/*
              A heading and not a control. §16.3 asks for grouping, not for disclosure, and
              `decisions/0031` is explicit about why: a shopkeeper who collapses a section and
              forgets is worse off than one who scrolls. `aria-labelledby` ties the list to it, so a
              screen reader reads "Counter, list, six items" rather than six lists of unnamed
              buttons.
            */}
            <h2 className="lm-rail__heading" id={`lm-rail-group-${group.id}`}>
              {group.label}
            </h2>
            <ul className="lm-rail__list" aria-labelledby={`lm-rail-group-${group.id}`}>
              {items.map((destination) => (
                <li key={destination.id}>{entry(destination, RAIL)}</li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="lm-main">
        <header className="lm-topbar">
          {/*
            The prominent left cluster, and the only control here that changes what every number on
            the screen means. §16.3 requires the branch always visible; `decisions/0031` makes it the
            loudest thing in the bar, because `Sign out` was.
          */}
          <div className="lm-topbar__branch">
            <span className="lm-topbar__branch-label">{t('shell.branch')}</span>
            {/* `LOC-005`: a branch name is the shop's own text in whatever script they typed it,
                so it takes its own direction rather than the page's. */}
            {branches !== undefined && branches.length > 1 && onBranchChange !== undefined ? (
              <select
                className="lm-input lm-topbar__branch-switch"
                aria-label={t('shell.branch')}
                dir="auto"
                value={currentBranchId ?? ''}
                onChange={(e) => {
                  onBranchChange(e.currentTarget.value)
                }}
              >
                {branches.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.name}
                  </option>
                ))}
              </select>
            ) : (
              <span className="lm-topbar__branch-name" dir="auto">
                {branchName}
              </span>
            )}
            {/* Nothing at all when it is not known: saying neither is honest, and a shopkeeper
                who needs to know has the Cash drawer screen. */}
            {cashSessionOpen === undefined ? null : cashSessionOpen ? (
              <span className="lm-badge lm-badge--open">{t('shell.drawerOpen')}</span>
            ) : (
              <span className="lm-badge">{t('shell.drawerClosed')}</span>
            )}
            {/*
              §16.5: "offline indicator with pending count, always visible when not clean". Beside
              the branch and the drawer because those are the other two facts about this till that
              are true wherever you are in the app.

              Injected rather than read here. The shell is a view over props and owns no transport,
              which is what lets it be rendered in a test without a queue: a component that reaches
              for a provider has made every caller provide one.
            */}
            {queueFlag}
          </div>
          <div className="lm-topbar__staff">
            {/* §5 ships both densities and until now one of them was unreachable: the attribute
                was hardcoded in `index.html` and no screen offered a choice. A select rather than
                a toggle because a toggle has to say which way is on, and "Rows: Tighter" says it
                without a label nobody reads.

                `aria-label` as well as the visible word, because below 48rem the word is hidden to
                get the bar down from four rows to one and a `display: none` label contributes
                nothing to an accessible name. */}
            <label className="lm-topbar__density">
              <span className="lm-topbar__density-label">{t('shell.density')}</span>
              <select
                className="lm-input"
                aria-label={t('shell.density')}
                value={density}
                onChange={(e) => onDensityChange(e.currentTarget.value as Density)}
              >
                {DENSITIES.map((option) => (
                  <option key={option} value={option}>
                    {t(
                      option === 'compact' ? 'shell.density.compact' : 'shell.density.comfortable',
                    )}
                  </option>
                ))}
              </select>
            </label>
            {/* `LOC-002`. Each language names itself and is never translated: a person looking
                for Urdu is looking for the word they would write, and "Urdu" in English is no use
                to somebody who cannot read the screen they are on. */}
            <label className="lm-topbar__language">
              <span className="lm-topbar__language-label">{t('shell.language')}</span>
              <select
                className="lm-input"
                aria-label={t('shell.language')}
                value={language}
                disabled={languagePending}
                onChange={(e) => onLanguageChange(e.currentTarget.value)}
              >
                <option value="">{t('shell.language.shop')}</option>
                {LOCALES.map((option) => (
                  <option key={option} value={option} lang={option}>
                    {t(option === 'ur' ? 'shell.language.ur' : 'shell.language.en')}
                  </option>
                ))}
              </select>
            </label>
            {/* Said where the choice is made. 125 of 1,933 keys have Urdu, and a product that
                hands somebody a mostly-English screen without saying so reads as broken rather
                than as unfinished. */}
            {language === 'ur' ? (
              <span className="lm-topbar__language-note">{t('shell.language.partial')}</span>
            ) : null}
            <span className="lm-topbar__staff-name" dir="auto">
              {staffName}
            </span>
            {/* A text button. It was `lm-button--secondary`, which made the loudest control on a
                shop's till the one that empties it. */}
            <button type="button" className="lm-topbar__signout" onClick={onSignOut}>
              {t('shell.signOut')}
            </button>
          </div>
        </header>

        <main className="lm-content">{children}</main>
      </div>

      {/*
        §16.3's bottom bar, shown below 48rem only. After `lm-main` in the DOM so a keyboard reaches
        the page before the navigation, and placed at the bottom by its own fixed position rather
        than by source order, so it stays there while a long screen scrolls under it.
      */}
      <nav className="lm-bar" aria-label={t('shell.bar')}>
        <ul className="lm-bar__list">
          {barItems.map((destination) => (
            <li key={destination.id}>{entry(destination, BAR)}</li>
          ))}
          <li>
            <button
              ref={moreButton}
              type="button"
              className={moreOpen ? 'lm-bar__item lm-bar__item--active' : 'lm-bar__item'}
              aria-expanded={moreOpen}
              onClick={() => {
                setMoreOpen(!moreOpen)
              }}
            >
              <span className="lm-bar__glyph" aria-hidden="true">
                {'⋯'}
              </span>
              <span className="lm-bar__label">{t('shell.more')}</span>
            </button>
          </li>
        </ul>
      </nav>

      {moreOpen ? (
        <div
          ref={sheet}
          className="lm-sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lm-sheet-heading"
          tabIndex={-1}
        >
          <div className="lm-sheet__head">
            <h2 className="lm-sheet__heading" id="lm-sheet-heading">
              {t('shell.sections')}
            </h2>
            <button
              type="button"
              className="lm-button lm-button--secondary"
              onClick={() => {
                setMoreOpen(false)
                moreButton.current?.focus()
              }}
            >
              {t('shell.moreClose')}
            </button>
          </div>
          {sections.map(({ group, items }) => (
            <div className="lm-sheet__group" key={group.id}>
              <h3 className="lm-sheet__group-heading" id={`lm-sheet-group-${group.id}`}>
                {group.label}
              </h3>
              <ul className="lm-sheet__list" aria-labelledby={`lm-sheet-group-${group.id}`}>
                {items.map((destination) => (
                  <li key={destination.id}>
                    <button
                      type="button"
                      className={
                        destination.id === current
                          ? 'lm-sheet__item lm-sheet__item--active'
                          : 'lm-sheet__item'
                      }
                      aria-current={destination.id === current ? 'page' : undefined}
                      onClick={() => {
                        navigate(destination.id)
                      }}
                    >
                      <span className="lm-sheet__glyph" aria-hidden="true">
                        {destination.glyph}
                      </span>
                      <span className="lm-sheet__label">{destination.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
